import ApiService from './api';
import type { Setting, SettingsResponse, SystemSettings } from '~/types/api';

/**
 * Site ayarları için API işlemleri
 */
export default class SettingsService {
  private static instance: SettingsService;
  private apiService: ApiService;
  private endpoint: string = '/public/settings';
  private systemEndpoint: string = '/public/systemSettings';

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  /**
   * Singleton örneği al
   */
  public static getInstance(): SettingsService {
    if (!SettingsService.instance) {
      SettingsService.instance = new SettingsService();
    }
    return SettingsService.instance;
  }

  /**
   * Tüm site ayarlarını getir
   * @param lang Dil kodu (örn: tr, en)
   */
  public async getAllSettings(lang: string = 'tr'): Promise<Setting[]> {
    try {
      const response = await this.apiService.get<Setting[]>(this.endpoint, { lang });
      
      if (response.status === 200 && response.data) {
        return response.data;
      }
      
      return [];
    } catch (error) {
      console.error('Site ayarları alınırken hata oluştu:', error);
      return [];
    }
  }

  /**
   * Belirli bir ayarı getir
   * @param key Ayar anahtarı
   * @param lang Dil kodu (örn: tr, en)
   */
  public async getSettingByKey(key: string, lang: string = 'tr'): Promise<Setting | null> {
    try {
      const allSettings = await this.getAllSettings(lang);
      const setting = allSettings.find(s => s.key === key);
      
      return setting || null;
    } catch (error) {
      console.error(`'${key}' ayarı alınırken hata oluştu:`, error);
      return null;
    }
  }

  /**
   * Sosyal medya ayarlarını getir
   * @param lang Dil kodu (örn: tr, en)
   */
  public async getSocialMediaSettings(lang: string = 'tr'): Promise<Record<string, string>> {
    try {
      const allSettings = await this.getAllSettings(lang);
      const socialMediaKeys = ['facebook', 'twitter', 'instagram', 'linkedin', 'whatsapp'];
      
      const socialMedia: Record<string, string> = {};
      
      socialMediaKeys.forEach(key => {
        const setting = allSettings.find(s => s.key === key);
        if (setting && typeof setting.value === 'string') {
          socialMedia[key] = setting.value;
        }
      });
      
      return socialMedia;
    } catch (error) {
      console.error('Sosyal medya ayarları alınırken hata oluştu:', error);
      return {};
    }
  }

  /**
   * İletişim bilgilerini getir
   * @param lang Dil kodu (örn: tr, en)
   */
  public async getContactInfo(lang: string = 'tr'): Promise<Record<string, any>> {
    try {
      const allSettings = await this.getAllSettings(lang);
      const contactKeys = ['address', 'phone', 'email', 'working_hours', 'maps'];
      
      const contactInfo: Record<string, any> = {};
      
      contactKeys.forEach(key => {
        const setting = allSettings.find(s => s.key === key);
        if (setting) {
          contactInfo[key] = setting.value;
        }
      });
      
      return contactInfo;
    } catch (error) {
      console.error('İletişim bilgileri alınırken hata oluştu:', error);
      return {};
    }
  }

  /**
   * Site genel ayarlarını getir
   * @param lang Dil kodu (örn: tr, en)
   */
  public async getSiteSettings(lang: string = 'tr'): Promise<Record<string, any>> {
    try {
      const allSettings = await this.getAllSettings(lang);
      const siteKeys = ['site_title', 'site_description', 'logo', 'dark_logo', 'favicon'];
      
      const siteSettings: Record<string, any> = {};
      
      siteKeys.forEach(key => {
        const setting = allSettings.find(s => s.key === key);
        if (setting) {
          siteSettings[key] = setting.value;
        }
      });
      
      return siteSettings;
    } catch (error) {
      console.error('Site genel ayarları alınırken hata oluştu:', error);
      return {};
    }
  }

  /**
   * Sistem ayarlarını getir (bakım modu vb.)
   */
  public async getSystemSettings(): Promise<SystemSettings | null> {
    try {
      const response = await this.apiService.get<SystemSettings>(this.systemEndpoint + '/getSystemSettings');
      
      if (response.status === 200 && response.data) {
        return response;
      }
      
      return null;
    } catch (error) {
      console.error('Sistem ayarları alınırken hata oluştu:', error);
      return null;
    }
  }
} 